// Availability Module
// Handles availability-related functionality

import { Modal } from 'bootstrap';

export class AvailabilityManager {
    constructor() {
        this.ajaxUrl = window.appraisal_ajax?.ajax_url || '/wp-admin/admin-ajax.php';
        this.nonce = window.appraisal_ajax?.nonce || '';
    }

    // Initialize availability manager
    init() {
        // Prevent double initialization
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        
        // Initialize AJAX configuration from data attributes
        this.initializeAjaxConfig();
        
        this.initializeAvailabilityForm();
    }

    // Initialize AJAX configuration from data attributes
    initializeAjaxConfig() {
        const ajaxConfig = document.getElementById('ajax-config');
        if (ajaxConfig) {
            const ajaxUrl = ajaxConfig.dataset.ajaxUrl;
            const nonce = ajaxConfig.dataset.nonce;
            
            if (ajaxUrl && nonce) {
                // Update the global AJAX object
                if (typeof window.appraisal_ajax === 'undefined') {
                    window.appraisal_ajax = {};
                }
                window.appraisal_ajax.ajax_url = ajaxUrl;
                window.appraisal_ajax.nonce = nonce;
                
                // Update local instance
                this.ajaxUrl = ajaxUrl;
                this.nonce = nonce;
            }
        }
    }

    // Add availability slot
    addAvailability(formData) {
        formData.append('action', 'add_availability');
        formData.append('nonce', this.nonce);

        return fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.showNotification('Availability slot added successfully!', 'success');
                return { success: true, data: data.data };
            } else {
                this.showNotification('Error: ' + data.data, 'danger');
                return { success: false, error: data.data };
            }
        })
        .catch(error => {
            console.error('Error:', error);
            this.showNotification('An error occurred. Please try again.', 'danger');
            return { success: false, error: error.message };
        });
    }

    // Delete availability slot
    deleteAvailability(availabilityId, forceDelete = false) {
        // Prevent multiple calls
        if (this.deletingAvailability) {
            return;
        }
        
        const confirmMessage = forceDelete 
            ? 'Are you sure you want to delete this availability slot? This will also cancel any associated appointments.'
            : 'Are you sure you want to delete this availability slot?';
            
        this.showDeleteConfirmation(confirmMessage, () => {
            this.performDelete(availabilityId, forceDelete);
        });
    }

    // Show custom delete confirmation modal
    showDeleteConfirmation(message, onConfirm) {
        // Remove any existing confirmation modal
        const existingModal = document.getElementById('deleteConfirmationModal');
        if (existingModal) {
            existingModal.remove();
        }

        // Create modal HTML
        const modalHtml = `
            <div class="modal fade" id="deleteConfirmationModal" tabindex="-1" aria-labelledby="deleteConfirmationModalLabel" aria-hidden="true">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="deleteConfirmationModalLabel">
                                <span class="fas fa-exclamation-triangle text-warning me-2"></span>
                                Confirm Deletion
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-0">${message}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                            <button type="button" class="btn btn-danger" id="confirmDeleteBtn">
                                <span class="fas fa-trash me-2"></span>Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add modal to page
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        // Show modal
        const modal = new Modal(document.getElementById('deleteConfirmationModal'));
        modal.show();

        // Handle confirm button click
        document.getElementById('confirmDeleteBtn').addEventListener('click', () => {
            modal.hide();
            onConfirm();
        });

        // Clean up modal when hidden
        document.getElementById('deleteConfirmationModal').addEventListener('hidden.bs.modal', () => {
            document.getElementById('deleteConfirmationModal').remove();
        });
    }

    // Perform the actual delete operation
    performDelete(availabilityId, forceDelete = false) {
        this.deletingAvailability = true;

        const formData = new FormData();
        formData.append('action', 'delete_availability');
        formData.append('availability_id', availabilityId);
        formData.append('nonce', this.nonce);
        if (forceDelete) {
            formData.append('force_delete', 'true');
        }

        fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => {
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Get response text first to see what we're actually getting
            return response.text();
        })
        .then(responseText => {
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                this.showNotification('Invalid response from server', 'danger');
                this.deletingAvailability = false;
                return;
            }
            
            // Check for success more explicitly
            if (data.success === true || data.success === 'true' || data.success === 1) {
                this.showNotification('Availability slot deleted successfully!', 'success');
                
                // Close modal first, before refreshing calendar
                const modalElement = document.getElementById('availabilityModal');
                if (modalElement) {
                    const modal = Modal.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    } else {
                        // If no instance exists, try to create one and hide it
                        const newModal = new Modal(modalElement);
                        newModal.hide();
                    }
                }
                
                // Refresh calendar and table after a short delay to allow modal to close
                setTimeout(() => {
                    if (window.appraisalTheme && window.appraisalTheme.calendarManager) {
                        window.appraisalTheme.calendarManager.refreshCalendar('appraiser');
                    }
                    // Also refresh the availability table
                    this.refreshAvailabilityTable();
                }, 300);
            } else {
                const errorMessage = data.data || data.message || 'Failed to delete availability slot.';
                this.showNotification(errorMessage, 'danger');
                
                // If the error mentions an appointment, offer force delete option
                if (errorMessage.includes('appointment') && !forceDelete) {
                    setTimeout(() => {
                        this.showDeleteConfirmation(
                            'Would you like to force delete this availability slot and cancel any associated appointments?',
                            () => this.deleteAvailability(availabilityId, true)
                        );
                    }, 2000);
                }
            }
        })
        .catch(error => {
            console.error('Delete error:', error);
            this.showNotification('An error occurred while deleting the availability slot.', 'danger');
        })
        .finally(() => {
            this.deletingAvailability = false;
        });
    }

    // Initialize availability form
    initializeAvailabilityForm() {
        // Prevent double initialization of form
        if (this.formInitialized) {
            return;
        }
        this.formInitialized = true;
        
        // Set minimum date to today
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        
        const startInput = document.getElementById('availability-start');
        const endInput = document.getElementById('availability-end');
        
        if (startInput) {
            startInput.min = todayStr;
        }
        if (endInput) {
            endInput.min = todayStr;
        }

        // Update end time when start time changes
        if (startInput && endInput) {
            startInput.addEventListener('change', function() {
                endInput.min = this.value;
            });
        }

        // Handle recurrence options
        this.initializeRecurrenceOptions();

        // Handle form submission
        const form = document.getElementById('availability-form');
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleAvailabilityFormSubmit(form);
            });
        }
    }

    // Handle availability form submission
    handleAvailabilityFormSubmit(form) {
        // Prevent double submission
        if (this.submittingForm) {
            return;
        }
        this.submittingForm = true;
        
        const formData = new FormData(form);
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.textContent;
        
        // Show loading state
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Adding...';
        
        this.addAvailability(formData)
        .then(result => {
            if (result.success) {
                form.reset();
                // Refresh calendar if available
                if (window.appraisalTheme && window.appraisalTheme.calendarManager) {
                    window.appraisalTheme.calendarManager.refreshCalendar('appraiser');
                }
                // Also refresh the availability table
                this.refreshAvailabilityTable();
            }
        })
        .finally(() => {
            // Reset button state
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
            this.submittingForm = false;
        });
    }

    // Initialize recurrence options
    initializeRecurrenceOptions() {
        const recurrenceRadios = document.querySelectorAll('input[name="recurrence"]');
        const recurrenceEndGroup = document.getElementById('recurrence-end-group');
        
        if (!recurrenceRadios.length || !recurrenceEndGroup) {
            return;
        }
        
        function handleRecurrenceChange() {
            const selectedRecurrence = document.querySelector('input[name="recurrence"]:checked');
            if (selectedRecurrence) {
                if (selectedRecurrence.value === 'none') {
                    recurrenceEndGroup.style.display = 'none';
                } else {
                    recurrenceEndGroup.style.display = 'block';
                }
            }
        }
        
        // Add event listeners to all recurrence radio buttons
        recurrenceRadios.forEach(radio => {
            radio.addEventListener('change', handleRecurrenceChange);
        });
        
        // Initialize the form state
        handleRecurrenceChange();
    }

    // Refresh availability table
    refreshAvailabilityTable() {
        // Find the table container
        const tableContainer = document.querySelector('.table-responsive table');
        if (!tableContainer) {
            return;
        }

        // Get the current page URL to fetch updated content
        const currentUrl = window.location.href;
        
        // Fetch the page content
        fetch(currentUrl)
        .then(response => response.text())
        .then(html => {
            // Create a temporary DOM element to parse the response
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Find the new table content
            const newTable = doc.querySelector('.table-responsive table');
            if (newTable) {
                // Replace the table content
                tableContainer.innerHTML = newTable.innerHTML;
            }
            
            // Also check if we need to show/hide the "no slots" message
            const noSlotsMessage = document.querySelector('.text-center.py-4');
            const newNoSlotsMessage = doc.querySelector('.text-center.py-4');
            
            if (newNoSlotsMessage && !newTable.querySelector('tbody tr')) {
                // Show no slots message
                if (!noSlotsMessage) {
                    const tableWrapper = tableContainer.closest('.dashboard-card');
                    if (tableWrapper) {
                        const tableResponsive = tableContainer.closest('.table-responsive');
                        tableResponsive.style.display = 'none';
                        
                        const noSlotsDiv = document.createElement('div');
                        noSlotsDiv.className = 'text-center py-4';
                        noSlotsDiv.innerHTML = newNoSlotsMessage.innerHTML;
                        tableWrapper.appendChild(noSlotsDiv);
                    }
                }
            } else if (noSlotsMessage && newTable.querySelector('tbody tr')) {
                // Hide no slots message and show table
                noSlotsMessage.remove();
                const tableResponsive = tableContainer.closest('.table-responsive');
                if (tableResponsive) {
                    tableResponsive.style.display = 'block';
                }
            }
        })
        .catch(error => {
            console.error('Error refreshing availability table:', error);
            // Fallback: reload the page
            window.location.reload();
        });
    }

    // Show notification
    showNotification(message, type = 'info') {
        if (window.appraisalTheme && window.appraisalTheme.showNotification) {
            window.appraisalTheme.showNotification(message, type);
        } else if (window.appraisalTheme && window.appraisalTheme.notificationManager) {
            // Direct access to notification manager
            window.appraisalTheme.notificationManager.show(message, type);
        } else {
            // Fallback to alert if notification system not available
            alert(message);
        }
    }
}

// Export singleton instance
export const availabilityManager = new AvailabilityManager();

